import { Button, Modal } from "antd";
import { FC } from "react";
import { Link } from "react-router-dom";
import { IPromotionCode } from "types/promotionCode";
import { formatDateTime } from "utils/datetime";
import { E_PROMOTION_CODE_TYPE } from "constants/promotionCode";

interface IPromotionCodeDetailModal {
  isOpen: boolean;
  onClose: () => void;
  promotionCode: IPromotionCode | null;
}

const PromotionCodeDetailModal: FC<IPromotionCodeDetailModal> = ({
  isOpen,
  onClose,
  promotionCode,
}) => {
  if (!promotionCode) return null;

  const isProductSpecific =
    promotionCode.products && promotionCode.products.length > 0;

  return (
    <Modal
      title={null}
      open={isOpen}
      onCancel={onClose}
      footer={null}
      centered
      width={500}
      className="promotion-detail-modal"
      styles={{
        body: { padding: 0 },
        content: { padding: 0, borderRadius: 16 },
      }}
    >
      <div className="bg-white rounded-2xl overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="text-xl font-bold text-gray-900">
            รายละเอียดการใช้ส่วนลดนี้
          </h2>
        </div>

        {/* Content */}
        <div className="px-6 py-6 space-y-6">
          {/* Discount Amount */}
          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-1">
              ลด {promotionCode.value}
              {promotionCode.type === E_PROMOTION_CODE_TYPE.PERCENTAGE
                ? "%"
                : "บาท"}
            </h3>
            <p className="text-gray-600">
              {promotionCode.name || promotionCode.code}
            </p>
          </div>

          {/* Promotion Code */}
          <div>
            <p className="text-gray-600 text-sm mb-1">โค้ดส่วนลด</p>
            <p className="font-semibold text-gray-900">{promotionCode.code}</p>
          </div>

          {/* Date Range */}
          <div>
            <p className="text-gray-600 text-sm mb-1">ระยะเวลา</p>
            <p className="font-semibold text-gray-900">
              {promotionCode.startDate && promotionCode.endDate
                ? `${formatDateTime(
                    promotionCode.startDate,
                    true
                  )} - ${formatDateTime(promotionCode.endDate, true)}`
                : promotionCode.endDate
                ? `ใช้ได้ถึง ${formatDateTime(promotionCode.endDate, true)}`
                : "ไม่มีวันหมดอายุ"}
            </p>
          </div>

          {/* Conditions */}
          <div>
            <p className="text-gray-600 text-sm mb-1">เงื่อนไข</p>
            <p className="font-semibold text-gray-900">
              {promotionCode.minOrderAmount
                ? `รับส่วนลด ${promotionCode.value}${
                    promotionCode.type === E_PROMOTION_CODE_TYPE.PERCENTAGE
                      ? "%"
                      : "บาท"
                  } ${
                    promotionCode.type === E_PROMOTION_CODE_TYPE.PERCENTAGE &&
                    promotionCode.maxDiscountAmount &&
                    +promotionCode.maxDiscountAmount < 1000000
                      ? `สูงสุด ฿${(+promotionCode.maxDiscountAmount).toLocaleString()}`
                      : ""
                  }`
                : `รับส่วนลด ${promotionCode.value}${
                    promotionCode.type === E_PROMOTION_CODE_TYPE.PERCENTAGE
                      ? "%"
                      : "บาท"
                  }`}
            </p>
            {promotionCode.minOrderAmount && (
              <p className="text-gray-600 mt-1">
                เมื่อซื้อขั้นต่ำ ฿
                {(+promotionCode.minOrderAmount).toLocaleString()}
              </p>
            )}
          </div>

          {/* Additional Details */}
          {(promotionCode.condition ||
            promotionCode.remark ||
            isProductSpecific) && (
            <div>
              <p className="text-gray-600 text-sm mb-1">รายละเอียดเพิ่มเติม</p>
              <div className="space-y-2">
                {promotionCode.condition && (
                  <p className="text-gray-900">{promotionCode.condition}</p>
                )}
                {promotionCode.remark && (
                  <p className="text-gray-900">{promotionCode.remark}</p>
                )}
                {isProductSpecific && (
                  <div>
                    <p className="text-gray-900 mb-2">สินค้าที่กำหนด:</p>
                    <div className="space-y-1">
                      {promotionCode.products.map((product) => (
                        <div key={product.id} className="text-gray-700 text-sm">
                          •{" "}
                          <Link
                            to={`/product/${product.id}`}
                            className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                          >
                            {product.name}
                          </Link>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer Button */}
        <div className="px-6 py-4 border-t border-gray-100">
          <Button type="primary" size="large" block onClick={onClose}>
            เข้าใจแล้ว
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default PromotionCodeDetailModal;
